import { useState, useEffect, useRef, useCallback } from 'react';
import API from '../api';

// ── 포스트잇(개인 메모) ────────────────────────────────────────────
// - 로그인한 본인이 작성한 메모만 자유 배치 보드에서 드래그 이동/크기조절/색상변경/접기가 된다.
// - "공유" 기능으로 특정 대상(회원 다중선택)에게만 열람권한을 줄 수 있다. 공유받은 메모는
//   내 화면 좌표와 무관하므로(작성자 화면 기준 좌표라 그대로 재사용하면 서로 배치가 꼬임) 보드
//   아래에 별도의 읽기전용 카드 목록으로 보여준다.
// - 접기 상태는 내 메모는 서버에 저장(다른 기기에서 봐도 유지), 공유받은 메모는 "내가 지금 이
//   화면에서 접어둔 것"뿐이라 로컬(state)에서만 관리한다.
// - 삭제하면 곧바로 완전삭제되지 않고 휴지통으로 이동한다. 휴지통에서 복원하거나, "휴지통 비우기"로
//   완전히 지울 수 있다.
// - 메모 상단 아이콘으로 겹침 순서를 한 단계씩 앞/뒤로 바꿀 수 있다(저장되는 값). 이와는 별개로,
//   겹쳐 있는 메모를 클릭하면 편집하기 편하도록 임시로 맨 앞에 보이고, 빈 보드 영역을 클릭하면
//   원래 저장된 겹침 순서로 돌아간다(이건 화면에만 적용되는 값이라 서버에 저장하지 않는다).

const COLORS = ['#FFF59D', '#F8BBD0', '#BBDEFB', '#C8E6C9', '#E1BEE7', '#FFE0B2', '#B2EBF2', '#FFFFFF'];
const SNAP = 10;
const MIN_W = 160, MIN_H = 140, MAX_W = 480, MAX_H = 480;
const FOLD_H = 40;
const ACTIVE_Z = 9999;
const BOARD_MIN_H = 520;

// 드래그/리사이즈가 끝난 위치를 주변 메모의 가장자리에 맞춰 자동정렬(스냅)한다.
function snapPosition(x, y, w, h, others) {
  let sx = x, sy = y, bestDX = SNAP, bestDY = SNAP;
  others.forEach(o => {
    const oL = o.pos_x, oR = o.pos_x + o.width, oT = o.pos_y, oB = o.pos_y + o.height;
    [[x, oL, oL], [x, oR, oR], [x + w, oR, oR - w], [x + w, oL, oL - w]].forEach(([a, b, resultX]) => {
      const d = Math.abs(a - b);
      if (d < bestDX) { bestDX = d; sx = resultX; }
    });
    [[y, oT, oT], [y, oB, oB], [y + h, oB, oB - h], [y + h, oT, oT - h]].forEach(([a, b, resultY]) => {
      const d = Math.abs(a - b);
      if (d < bestDY) { bestDY = d; sy = resultY; }
    });
  });
  return { x: Math.max(0, sx), y: Math.max(0, sy) };
}

function NoteCard({ note, others, onSaveNote, onSavePosition, onCollapse, onDelete, onOpenShare,
  onBringForward, onSendBackward, isActive, onActivate }) {
  const [local, setLocal] = useState({ x: note.pos_x, y: note.pos_y, w: note.width, h: note.height });
  useEffect(() => { setLocal({ x: note.pos_x, y: note.pos_y, w: note.width, h: note.height }); }, [note.pos_x, note.pos_y, note.width, note.height]);
  const [title, setTitle] = useState(note.title || '');
  useEffect(() => { setTitle(note.title || ''); }, [note.title]);
  const [text, setText] = useState(note.content);
  useEffect(() => { setText(note.content); }, [note.content]);
  const [color, setColor] = useState(note.color);
  useEffect(() => { setColor(note.color); }, [note.color]);
  const [showPalette, setShowPalette] = useState(false);

  const dragRef = useRef(null);
  const resizeRef = useRef(null);

  const onDragMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    if (Math.abs(e.clientX - d.mx0) > 3 || Math.abs(e.clientY - d.my0) > 3) d.moved = true;
    setLocal(l => ({ ...l, x: Math.max(0, d.x0 + (e.clientX - d.mx0)), y: Math.max(0, d.y0 + (e.clientY - d.my0)) }));
  }, []);
  // 접힌 상태(압정)에서는 클릭=펼치기, 드래그=이동으로 구분해야 하므로, 실제로 마우스가 움직였는지
  // (moved) 기록해뒀다가 뗄 때 판단한다 — 거의 안 움직였으면 클릭으로 보고 펼치고, 움직였으면
  // 이동으로 보고 새 위치를 저장한다(펼치지 않음).
  const stopDrag = useCallback(() => {
    const wasCollapsed = note.collapsed;
    const moved = dragRef.current ? dragRef.current.moved : false;
    dragRef.current = null;
    window.removeEventListener('mousemove', onDragMove);
    window.removeEventListener('mouseup', stopDrag);
    if (wasCollapsed) {
      if (!moved) { onCollapse(note.id, false); return; }
      setLocal(l => {
        onSavePosition(note.id, { pos_x: l.x, pos_y: l.y, width: l.w, height: l.h });
        return l;
      });
      return;
    }
    setLocal(l => {
      const snapped = snapPosition(l.x, l.y, l.w, l.h, others);
      onSavePosition(note.id, { pos_x: snapped.x, pos_y: snapped.y, width: l.w, height: l.h });
      return { ...l, x: snapped.x, y: snapped.y };
    });
  }, [onDragMove, others, note.id, note.collapsed, onSavePosition, onCollapse]);
  const startDrag = (e) => {
    e.preventDefault();
    onActivate(note.id);
    dragRef.current = { x0: local.x, y0: local.y, mx0: e.clientX, my0: e.clientY, moved: false };
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', stopDrag);
  };

  const onResizeMove = useCallback((e) => {
    const r = resizeRef.current;
    if (!r) return;
    setLocal(l => ({
      ...l,
      w: Math.min(MAX_W, Math.max(MIN_W, r.w0 + (e.clientX - r.mx0))),
      h: Math.min(MAX_H, Math.max(MIN_H, r.h0 + (e.clientY - r.my0))),
    }));
  }, []);
  const stopResize = useCallback(() => {
    resizeRef.current = null;
    window.removeEventListener('mousemove', onResizeMove);
    window.removeEventListener('mouseup', stopResize);
    setLocal(l => {
      const snapped = snapPosition(l.x, l.y, l.w, l.h, others);
      onSavePosition(note.id, { pos_x: snapped.x, pos_y: snapped.y, width: l.w, height: l.h });
      return { ...l, x: snapped.x, y: snapped.y };
    });
  }, [onResizeMove, others, note.id, onSavePosition]);
  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onActivate(note.id);
    resizeRef.current = { w0: local.w, h0: local.h, mx0: e.clientX, my0: e.clientY };
    window.addEventListener('mousemove', onResizeMove);
    window.addEventListener('mouseup', stopResize);
  };

  useEffect(() => () => {
    window.removeEventListener('mousemove', onDragMove);
    window.removeEventListener('mouseup', stopDrag);
    window.removeEventListener('mousemove', onResizeMove);
    window.removeEventListener('mouseup', stopResize);
  }, [onDragMove, stopDrag, onResizeMove, stopResize]);

  const pickColor = (c) => {
    setColor(c);
    setShowPalette(false);
    onSaveNote(note.id, { title, content: text, color: c });
  };
  const saveAll = () => onSaveNote(note.id, { title, content: text, color });
  const activate = () => onActivate(note.id);
  const withActivate = (fn) => (e) => { activate(); if (fn) fn(e); e.stopPropagation(); };

  if (note.collapsed) {
    return (
      <div className="sticky-note-card" onMouseDown={startDrag}
        title={note.title || note.content || '(내용 없음)'}
        style={{ position: 'absolute', left: local.x, top: local.y, height: FOLD_H, minWidth: 90, maxWidth: 180,
          zIndex: isActive ? ACTIVE_Z : note.z_index,
          background: color, border: '1px solid rgba(0,0,0,0.1)', borderRadius: 20, cursor: 'move',
          display: 'flex', alignItems: 'center', gap: 6, padding: '0 12px', userSelect: 'none',
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>
        <span style={{ fontSize: 16, flexShrink: 0 }}>📌</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {note.title || '(제목 없음)'}
        </span>
      </div>
    );
  }

  return (
    <div className="sticky-note-card" onMouseDown={activate}
      style={{ position: 'absolute', left: local.x, top: local.y, width: local.w, height: local.h,
        zIndex: isActive ? ACTIVE_Z : note.z_index,
        background: color, border: '1px solid rgba(0,0,0,0.1)', borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div onMouseDown={startDrag}
        style={{ cursor: 'move', padding: '6px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'rgba(0,0,0,0.06)', fontSize: 11, color: '#555', userSelect: 'none' }}>
        <span>🗒️ {note.updated_at ? note.updated_at.slice(5, 16) : ''}</span>
        <div style={{ display: 'flex', gap: 4 }} onMouseDown={withActivate()}>
          <button onClick={() => onSendBackward(note.id)} title="뒤로 보내기"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12 }}>⬇️</button>
          <button onClick={() => onBringForward(note.id)} title="앞으로 보내기"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12 }}>⬆️</button>
          <button onClick={() => setShowPalette(v => !v)} title="색상변경"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12 }}>🎨</button>
          <button onClick={() => onOpenShare(note)} title="공유"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12 }}>👥</button>
          <button onClick={() => onCollapse(note.id, true)} title="접기"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12 }}>➖</button>
          <button onClick={() => onDelete(note.id)} title="삭제(휴지통으로 이동)"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, color: '#c0392b' }}>✕</button>
        </div>
      </div>
      {showPalette && (
        <div style={{ display: 'flex', gap: 4, padding: '4px 8px', background: 'rgba(255,255,255,0.5)' }} onMouseDown={withActivate()}>
          {COLORS.map(c => (
            <div key={c} onClick={() => pickColor(c)}
              style={{ width: 16, height: 16, borderRadius: '50%', background: c, cursor: 'pointer',
                border: c === color ? '2px solid #333' : '1px solid #ccc' }} />
          ))}
        </div>
      )}
      <input value={title} onChange={e => setTitle(e.target.value)} onBlur={saveAll}
        placeholder="메모 이름" onMouseDown={withActivate()}
        style={{ border: 'none', outline: 'none', background: 'transparent', padding: '6px 10px 0',
          fontSize: 13, fontWeight: 700, fontFamily: 'inherit', color: '#333' }} />
      <textarea value={text} onChange={e => setText(e.target.value)} onBlur={saveAll}
        placeholder="메모를 입력하세요..." onMouseDown={withActivate()}
        style={{ flex: 1, resize: 'none', border: 'none', outline: 'none', background: 'transparent',
          padding: '4px 10px 8px', fontSize: 13, fontFamily: 'inherit', color: '#333' }} />
      {note.shares && note.shares.length > 0 && (
        <div style={{ padding: '2px 8px 6px', fontSize: 10, color: '#666' }}>
          👥 공유중 {note.shares.length}명 ({note.shares.map(s => s.name || s.username).join(', ')})
        </div>
      )}
      <div onMouseDown={startResize} title="크기조절"
        style={{ position: 'absolute', right: 0, bottom: 0, width: 16, height: 16, cursor: 'nwse-resize' }}>
        <div style={{ position: 'absolute', right: 3, bottom: 3, width: 8, height: 8,
          borderRight: '2px solid rgba(0,0,0,0.3)', borderBottom: '2px solid rgba(0,0,0,0.3)' }} />
      </div>
    </div>
  );
}

export default function StickyNotes() {
  const [data, setData] = useState({ own: [], sharedWithMe: [] });
  const [loading, setLoading] = useState(true);
  const [shareNote, setShareNote] = useState(null);
  const [users, setUsers] = useState([]);
  const [checkedUsers, setCheckedUsers] = useState([]);
  const [sharedCollapsed, setSharedCollapsed] = useState({});
  const [trash, setTrash] = useState([]);
  const [trashOpen, setTrashOpen] = useState(false);
  const [activeNoteId, setActiveNoteId] = useState(null); // 겹친 메모를 클릭하면 화면상으로만 맨 앞에 표시
  const [creating, setCreating] = useState(false); // 새 메모 생성 요청이 겹치는 것을 막기 위한 플래그
  const boardRef = useRef(null);

  const load = () => {
    setLoading(true);
    API.get('/sticky-notes').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  const loadTrash = () => {
    API.get('/sticky-notes/trash').then(r => setTrash(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); loadTrash(); }, []);

  // 한 줄에 항상 4개씩 배치되도록, 보드의 실제 렌더 폭을 기준으로 카드 크기를 계산한다
  // (화면/창 크기가 다르면 카드 크기도 그에 맞춰 커지거나 작아짐). 세로 높이는 기존 비율 대비
  // 40% 줄여서(*0.6) 화면에 더 많은 메모가 한눈에 들어오도록 한다.
  const NOTE_GAP = 16;
  // 좌측 상단부터 4열 그리드를 훑어서, 기존 메모(자유 배치로 옮겨졌을 수도 있음)와 겹치지 않는
  // 첫 번째 빈 자리를 찾는다. 다 겹치면(그리드가 꽉 차면) 가장 아래쪽에 새로 쌓는다.
  const findFreeSpot = (width, height, own) => {
    for (let row = 0; row < 200; row++) {
      for (let col = 0; col < 4; col++) {
        const x = NOTE_GAP + col * (width + NOTE_GAP);
        const y = NOTE_GAP + row * (height + NOTE_GAP);
        const overlaps = own.some(n => (
          x < n.pos_x + n.width && x + width > n.pos_x &&
          y < n.pos_y + n.height && y + height > n.pos_y
        ));
        if (!overlaps) return { x, y };
      }
    }
    const bottom = own.reduce((m, n) => Math.max(m, n.pos_y + n.height), 0);
    return { x: NOTE_GAP, y: bottom + NOTE_GAP };
  };
  // 새 메모의 가로/세로 크기는 "보드에 이미 놓인 메모가 있으면 그 크기를 그대로 따라간다."
  // (매번 boardRef.clientWidth를 다시 재서 크기를 계산하면, 스크롤바 유무 등으로 픽셀 단위가
  // 살짝 달라질 때 이미 저장된 메모의 격자와 새 메모의 격자 폭이 어긋나서 빈 칸이 생길 수 있다.
  // 기존 메모의 크기를 그대로 재사용하면 격자가 항상 딱 맞는다.) 보드에 메모가 하나도 없을 때만
  // 화면 폭을 기준으로 새로 계산한다.
  const addNote = () => {
    if (creating) return; // 버튼을 빠르게 연타해도 이전 생성 요청이 끝나기 전엔 새 요청을 막는다
    setCreating(true);
    let width, height;
    if (data.own.length > 0) {
      width = data.own[0].width;
      height = data.own[0].height;
    } else {
      const boardWidth = boardRef.current ? boardRef.current.clientWidth : 1600;
      const rawW = (boardWidth - NOTE_GAP * 5) / 4;
      width = Math.round(Math.min(MAX_W, Math.max(MIN_W, rawW)));
      height = Math.round(Math.min(MAX_H, Math.max(MIN_H, width * (200 / 220) * 0.6)));
    }
    const { x, y } = findFreeSpot(width, height, data.own);
    API.post('/sticky-notes', {
      title: '', content: '', color: COLORS[0],
      pos_x: x, pos_y: y, width, height,
    }).then(() => load()).finally(() => setCreating(false));
  };

  // 저장 API가 실패하면 낙관적으로 바꿔둔 화면 상태가 서버 상태와 어긋난 채로 굳어버릴 수 있다.
  // 실패 시 항상 load()로 서버 최신값을 다시 받아와 화면을 재동기화한다(2026-09, 접기 후 메모가
  // 사라진 것처럼 보이는 버그류의 근본 원인 - 실패가 조용히 무시되던 문제 수정).
  const saveNote = (id, patch) => {
    setData(d => ({ ...d, own: d.own.map(n => (n.id === id ? { ...n, ...patch } : n)) }));
    API.put(`/sticky-notes/${id}`, patch).catch(() => load());
  };
  const savePosition = (id, patch) => {
    setData(d => ({ ...d, own: d.own.map(n => (n.id === id ? { ...n, ...patch } : n)) }));
    API.put(`/sticky-notes/${id}/position`, patch).catch(() => load());
  };
  const toggleCollapse = (id, collapsed) => {
    setData(d => ({ ...d, own: d.own.map(n => (n.id === id ? { ...n, collapsed: collapsed ? 1 : 0 } : n)) }));
    API.patch(`/sticky-notes/${id}/collapse`, { collapsed }).catch(() => load());
  };
  // 제목/내용이 둘 다 비어있는 메모는 안내문구 없이, 휴지통도 거치지 않고 바로 완전삭제한다.
  const removeNote = (id) => {
    const note = data.own.find(n => n.id === id);
    const isEmpty = note && !(note.title || '').trim() && !(note.content || '').trim();
    if (!isEmpty && !window.confirm('이 메모를 휴지통으로 보낼까요?')) return;
    setData(d => ({ ...d, own: d.own.filter(n => n.id !== id) }));
    if (isEmpty) {
      API.delete(`/sticky-notes/${id}`).then(() => API.delete(`/sticky-notes/${id}/permanent`)).then(loadTrash).catch(() => load());
    } else {
      API.delete(`/sticky-notes/${id}`).then(loadTrash).catch(() => load());
    }
  };
  // 내 메모 전체를 한번에 휴지통으로 보낸다(개별 삭제와 동일하게 소프트 삭제 → 휴지통에서 복원 가능).
  // Promise.all은 한 건이라도 실패하면 즉시 reject되어 나머지 성공분까지 화면에서 사라진 채로
  // 방치되고(성공한 삭제는 서버에 반영됐는데 목록만 되돌아옴), 실패했다는 사실도 알 수 없었다.
  // Promise.allSettled로 바꿔 성공/실패와 무관하게 항상 최신 목록을 다시 불러오고, 일부라도
  // 실패하면 몇 건이 실패했는지 알려준다(2026-09, "전체삭제 시 일부 메모가 남아있다" 버그 수정).
  const removeAllNotes = () => {
    if (data.own.length === 0) return;
    if (!window.confirm(`현재 메모 ${data.own.length}개를 모두 휴지통으로 보낼까요?`)) return;
    const ids = data.own.map(n => n.id);
    setData(d => ({ ...d, own: [] }));
    Promise.allSettled(ids.map(id => API.delete(`/sticky-notes/${id}`))).then(results => {
      const failCount = results.filter(r => r.status === 'rejected').length;
      load();
      loadTrash();
      if (failCount > 0) {
        alert(`${ids.length}개 중 ${failCount}개는 삭제에 실패했습니다. 다시 시도해주세요.`);
      }
    });
  };
  // 공유받은 메모를 내 목록에서 뺀다 — 소유자 쪽 "공유중" 목록에서도 함께 사라진다.
  const removeSharedNote = (id) => {
    if (!window.confirm('공유받은 메모를 목록에서 삭제할까요? (상대방의 공유 목록에서도 함께 제거됩니다)')) return;
    setData(d => ({ ...d, sharedWithMe: d.sharedWithMe.filter(n => n.id !== id) }));
    API.delete(`/sticky-notes/${id}/share/me`).catch(() => load());
  };
  // 접혀있던(압정 모양) 내 메모/공유받은 메모를 한번에 모두 펼친다.
  const hasCollapsed = data.own.some(n => n.collapsed) || data.sharedWithMe.some(n => sharedCollapsed[n.id]);
  const expandAll = () => {
    data.own.forEach(n => { if (n.collapsed) toggleCollapse(n.id, false); });
    setSharedCollapsed({});
  };

  // ── 겹침 순서(헤더 아이콘: 한 단계씩 앞/뒤로) ──────────────────────────
  const moveZOrder = (noteId, dir) => {
    const note = data.own.find(n => n.id === noteId);
    if (!note) return;
    const others = data.own.filter(n => n.id !== noteId);
    let z = note.z_index;
    if (dir === 'forward') {
      const higher = others.filter(o => o.z_index > note.z_index).map(o => o.z_index);
      if (higher.length) z = Math.min(...higher) + 1;
      else z = Math.max(note.z_index, ...others.map(o => o.z_index), 0) + 1;
    } else {
      const lower = others.filter(o => o.z_index < note.z_index).map(o => o.z_index);
      if (lower.length) z = Math.max(...lower) - 1;
      else z = Math.min(note.z_index, ...others.map(o => o.z_index), 0) - 1;
    }
    if (z === note.z_index) return;
    setData(d => ({ ...d, own: d.own.map(n => (n.id === noteId ? { ...n, z_index: z } : n)) }));
    API.patch(`/sticky-notes/${noteId}/zindex`, { z_index: z }).catch(() => load());
  };
  const bringForward = (id) => moveZOrder(id, 'forward');
  const sendBackward = (id) => moveZOrder(id, 'backward');

  // 겹친 메모를 클릭하면 화면상으로만 맨 앞에 보이게 하고(activeNoteId), 노트가 아닌 빈 영역을
  // 클릭하면 원래 저장된 겹침 순서로 되돌린다.
  const handleRootMouseDown = (e) => {
    if (!e.target.closest('.sticky-note-card')) setActiveNoteId(null);
  };

  // ── 휴지통 ──────────────────────────────────────────────────
  // 아래 세 함수 모두 실패 시 이전에는 아무 반응 없이 조용히 끝났다 - 사용자는 복원/삭제가 됐는지
  // 안 됐는지 알 수 없고, 휴지통 목록도 재조회되지 않아 실제 서버 상태와 어긋난 채로 남아있었다.
  // 실패해도 항상 휴지통을 재조회하고 오류를 알려주도록 수정(2026-09, "전체삭제 후 복원 안됨" 버그 수정).
  const openTrash = () => { setTrashOpen(true); loadTrash(); };
  const restoreNote = (id) => {
    API.patch(`/sticky-notes/${id}/restore`).then(() => { load(); loadTrash(); })
      .catch((e) => { loadTrash(); alert(e?.response?.data?.message || '복원에 실패했습니다. 다시 시도해주세요.'); });
  };
  const permanentDelete = (id) => {
    if (!window.confirm('완전히 삭제할까요? 복구할 수 없습니다.')) return;
    API.delete(`/sticky-notes/${id}/permanent`).then(loadTrash)
      .catch((e) => { loadTrash(); alert(e?.response?.data?.message || '삭제에 실패했습니다. 다시 시도해주세요.'); });
  };
  const emptyTrash = () => {
    if (trash.length === 0) return;
    if (!window.confirm(`휴지통을 비우면 ${trash.length}개의 메모가 완전히 삭제되며 복구할 수 없습니다. 계속할까요?`)) return;
    API.delete('/sticky-notes/trash/empty').then(loadTrash)
      .catch((e) => { loadTrash(); alert(e?.response?.data?.message || '휴지통 비우기에 실패했습니다. 다시 시도해주세요.'); });
  };

  const openShare = (note) => {
    setShareNote(note);
    setCheckedUsers((note.shares || []).map(s => (s && s.username) || s));
    if (users.length === 0) {
      API.get('/sticky-notes/users/list').then(r => setUsers(r.data || [])).catch(() => {});
    }
  };
  const saveShare = () => {
    API.put(`/sticky-notes/${shareNote.id}/share`, { usernames: checkedUsers })
      .then(() => { setShareNote(null); load(); })
      .catch(() => {});
  };

  const boardHeight = Math.max(
    BOARD_MIN_H,
    ...data.own.map(n => (n.collapsed ? n.pos_y + FOLD_H : n.pos_y + n.height) + 40),
  );

  return (
    <div style={{ padding: 24, maxWidth: 1690 }} onMouseDown={handleRootMouseDown}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>🗒️ 포스트잇</h2>
        <div className="hdr-actions" style={{ display: 'flex', gap: 8 }}>
          <button onClick={openTrash} title="휴지통"
            style={{ position: 'relative', background: '#fff', border: '1px solid #ddd', borderRadius: 8,
              padding: '10px 14px', fontSize: 14, cursor: 'pointer' }}>
            🗑️
            {trash.length > 0 && (
              <span style={{ position: 'absolute', top: -6, right: -6, background: '#c0392b', color: '#fff',
                borderRadius: '50%', width: 18, height: 18, fontSize: 10, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontWeight: 700 }}>{trash.length}</span>
            )}
          </button>
          <button onClick={expandAll} disabled={!hasCollapsed} title="접혀있거나 압정으로 숨겨진 메모를 모두 펼칩니다"
            style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 8,
              padding: '10px 14px', fontSize: 14, fontWeight: 600,
              cursor: hasCollapsed ? 'pointer' : 'not-allowed', opacity: hasCollapsed ? 1 : 0.5 }}>
            전체보기
          </button>
          <button onClick={removeAllNotes} disabled={data.own.length === 0}
            style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 8,
              padding: '10px 14px', fontSize: 14, fontWeight: 600, color: '#c0392b',
              cursor: data.own.length === 0 ? 'not-allowed' : 'pointer', opacity: data.own.length === 0 ? 0.5 : 1 }}>
            전체삭제
          </button>
          <button onClick={addNote} disabled={creating}
            style={{ background: '#FBC02D', color: '#4a3900', border: 'none', borderRadius: 8,
              padding: '10px 18px', fontSize: 14, fontWeight: 700,
              cursor: creating ? 'not-allowed' : 'pointer', opacity: creating ? 0.6 : 1 }}>
            + 새 메모
          </button>
        </div>
      </div>
      <p style={{ fontSize: 13, color: '#888', margin: '4px 0 14px' }}>
        본인이 작성한 메모만 보이며, 공유한 대상에게만 별도로 열람 권한을 줄 수 있습니다.
      </p>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#aaa' }}>불러오는 중...</div>
          ) : (
            <div ref={boardRef} style={{ position: 'relative', width: '100%', minHeight: boardHeight,
              background: '#f1f5f9', border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 28 }}>
              {data.own.length === 0 && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: '#aaa', fontSize: 13 }}>
                  등록된 메모가 없습니다. "+ 새 메모"로 시작해보세요.
                </div>
              )}
              {data.own.map(n => (
                <NoteCard key={n.id} note={n} others={data.own.filter(o => o.id !== n.id)}
                  onSaveNote={saveNote} onSavePosition={savePosition} onCollapse={toggleCollapse}
                  onDelete={removeNote} onOpenShare={openShare}
                  onBringForward={bringForward} onSendBackward={sendBackward}
                  isActive={activeNoteId === n.id} onActivate={setActiveNoteId} />
              ))}
            </div>
          )}
        </div>

        {/* 공유받은 메모 - 화면 오른쪽 끝에 세로로 나열. 작성자 좌표는 내 화면과 무관하므로
            자유배치 보드가 아닌 별도의 읽기전용 세로 목록으로 보여준다. */}
        {data.sharedWithMe.length > 0 && (
          <div style={{ width: 200, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h3 style={{ fontSize: 13, fontWeight: 600, margin: '0 0 2px', color: '#333' }}>👥 공유받은 메모</h3>
            {data.sharedWithMe.map(n => {
              const collapsed = !!sharedCollapsed[n.id];
              const who = [n.owner_department, n.owner_position, n.owner_name || n.owner_username].filter(Boolean).join(' ');
              return collapsed ? (
                <div key={n.id} onClick={() => setSharedCollapsed(s => ({ ...s, [n.id]: false }))}
                  title={`${who} · ${n.title || n.content || '(내용 없음)'}`}
                  style={{ width: '100%', height: 40, background: n.color, border: '1px solid rgba(0,0,0,0.1)',
                    borderRadius: 20, display: 'flex', alignItems: 'center', gap: 6, padding: '0 12px',
                    cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>📌</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#333', overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{who}</span>
                </div>
              ) : (
                <div key={n.id} style={{ width: '100%', minHeight: 160, background: n.color,
                  border: '1px solid rgba(0,0,0,0.1)', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <div style={{ padding: '6px 8px', background: 'rgba(0,0,0,0.06)', fontSize: 11, color: '#555' }}>
                    <div style={{ fontWeight: 700, color: '#333' }}>👤 {who}</div>
                  </div>
                  <div style={{ flex: 1, padding: '8px 10px', fontSize: 13, color: '#333', whiteSpace: 'pre-wrap', overflowY: 'auto' }}>
                    {n.title && <div style={{ fontWeight: 700, marginBottom: 4 }}>{n.title}</div>}
                    {n.content || <span style={{ color: '#999' }}>(내용 없음)</span>}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, padding: '4px 8px', background: 'rgba(255,255,255,0.4)' }}>
                    <button onClick={() => setSharedCollapsed(s => ({ ...s, [n.id]: true }))} title="접기"
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12 }}>➖</button>
                    <button onClick={() => removeSharedNote(n.id)} title="삭제"
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, color: '#c0392b' }}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 공유 대상 선택 모달 */}
      {shareNote && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShareNote(null)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, padding: 24, width: 360, maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>메모 공유 대상 선택</h3>
            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
              {users.length === 0 ? (
                <div style={{ color: '#aaa', fontSize: 13, padding: 8 }}>불러오는 중...</div>
              ) : users.map(u => (
                <label key={u.username} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={checkedUsers.includes(u.username)}
                    onChange={e => setCheckedUsers(cs => (e.target.checked ? [...cs, u.username] : cs.filter(x => x !== u.username)))} />
                  <span>{u.department ? `${u.department} ` : ''}{u.position ? `${u.position} ` : ''}{u.name}</span>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={() => setShareNote(null)}
                style={{ background: '#f1f5f9', border: '1px solid #ddd', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}>취소</button>
              <button onClick={saveShare}
                style={{ background: '#185FA5', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>저장</button>
            </div>
          </div>
        </div>
      )}

      {/* 휴지통 모달 */}
      {trashOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setTrashOpen(false)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, padding: 24, width: 420, maxHeight: '75vh', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>🗑️ 휴지통</h3>
            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
              {trash.length === 0 ? (
                <div style={{ color: '#aaa', fontSize: 13, padding: 20, textAlign: 'center' }}>휴지통이 비어있습니다.</div>
              ) : trash.map(n => (
                <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 6px',
                  borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ width: 14, height: 14, borderRadius: '50%', background: n.color, flexShrink: 0,
                    border: '1px solid rgba(0,0,0,0.15)' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {n.title || '(제목 없음)'}
                    </div>
                    <div style={{ fontSize: 11, color: '#999' }}>{n.deleted_at ? n.deleted_at.slice(0, 16) : ''} 삭제됨</div>
                  </div>
                  <button onClick={() => restoreNote(n.id)}
                    style={{ background: '#eef2ff', border: '1px solid #c7d2fe', color: '#185FA5', borderRadius: 6,
                      padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>복원</button>
                  <button onClick={() => permanentDelete(n.id)}
                    style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#c0392b', borderRadius: 6,
                      padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>영구삭제</button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 16 }}>
              <button onClick={emptyTrash} disabled={trash.length === 0}
                style={{ background: trash.length === 0 ? '#f1f5f9' : '#c0392b', color: trash.length === 0 ? '#aaa' : '#fff',
                  border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600,
                  cursor: trash.length === 0 ? 'not-allowed' : 'pointer' }}>휴지통 비우기</button>
              <button onClick={() => setTrashOpen(false)}
                style={{ background: '#f1f5f9', border: '1px solid #ddd', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}>닫기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
